import { Controller } from "@hotwired/stimulus";

import {
  Chart,
  LineController,
  PieController,
  DoughnutController,
  LinearScale,
  CategoryScale,
  ArcElement,
  LineElement,
  PointElement,
  TimeScale,
  Legend,
  Title,
  Tooltip,
} from 'chart.js';

import 'chartjs-adapter-date-fns';

Chart.register(
  LineController,
  PieController,
  DoughnutController,
  LinearScale,
  CategoryScale,
  ArcElement,
  LineElement,
  PointElement,
  TimeScale,
  Legend,
  Title,
  Tooltip,
)

class CustomChart {
  constructor({ selector }) {
    this.selector = selector;
    this.refs = {
      chartType: document.querySelector(`${this.selector}`).dataset.chartType,
      chartData: document.querySelector(`${this.selector}`).dataset.chartData,
    }
  }

  getChartOptions(type) {
    if (type.toLowerCase() === 'line'){
      return {
        tension: 0.3,
        borderWidth: 5,
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'nearest',
          intersect: false,
        },
        elements: {
          point: {
            radius: 0,
            hoverRadius: 8,
            backgroundColor: 'rgb(255,255,255)',
            hoverBorderWidth: 2,
          }
        },
        plugins: {
          tooltip: {
            enabled: true,
            padding: 10,
            displayColors: false,
            caretSize: 8,
            caretPadding: 10,
            backgroundColor: (item) => {
              return item.tooltip.labelColors[0].borderColor;
            },
            titleColor: (item) => {
              if (item.tooltip) {
                if (item.tooltip.labelColors[0].borderColor === 'rgb(234,187,0)') {
                  return 'black';
                }
                return 'white';
              }
            },
            bodyColor: (item) => {
              if (item.tooltip) {
                if (item.tooltip.labelColors[0].borderColor === 'rgb(234,187,0)') {
                  return 'black';
                }
                return 'white';
              }
            },
            bodyFont: {
              family: '',
            }
          },
        },
        scales: {
          x: {
            type: 'time',
            time: {
              min: new Date('2016-01-01'),
              max: new Date(),
              unit: 'year',
              round: 'month',
              tooltipFormat: 'MMM yyyy',
            },
            grid: {
              display: false,
              drawTicks: true
            },
            bounds: 'data',
          },
          y: {
            grid: {
              color: 'rgb(205,205,205)',
              drawTicks: true,
            },
            ticks: {

            },
            position: 'right',
            beginAtZero: true,
          }
        }
      }
    }
    if (type.toLowerCase() === 'pie'){
      return {
        cutout: 0,
        borderColor: '#EDEDED',
        borderWidth: 5,
        borderRadius: 5,
        maintainAspectRatio: true,
        responsive: true,
        elements: {
          
        },
        plugins: {
          legend: {
            display: false
          }
        },
        title: {
          display: false
        },
      }
    }
    if (type.toLowerCase() === 'doughnut'){
      return {
        cutout: '70%',
        borderColor: 'transparent',
        borderWidth: 0,
        borderRadius: 0,
        elements: {
        },
        datasets: {
          weight: 2,
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            enabled: false
          },
          test: {

          }
        },
        title: {
          display: false
        },
      }
    }
  }
  
  getChartPlugins() {
    let plugins = [];

    // If custom legend is detected
    if (document.querySelector(`${this.selector} .legend-container`)) {

      const getOrCreateLegendList = (chart, id) => {
        const legendContainer = document.querySelector(`#${id}`);
        let listContainer = legendContainer.querySelector('ul')
        if (!listContainer) {
          listContainer = document.createElement('ul');
          listContainer.classList = "flex flex-row flex-wrap gap-2.5";
          legendContainer.appendChild(listContainer);
        }
        return listContainer;
      }

      const htmlLegendPlugin = {
        id: 'htmlLegend',
        afterUpdate(chart, args, options) {
          const legends = options.containerID
          for (let i = 0; i < legends.length; i++) {
            const ul = getOrCreateLegendList(chart, legends[i]);
            while (ul.firstChild) {
              ul.firstChild.remove();
            }
            const items = chart.options.plugins.legend.labels.generateLabels(chart);
            items.forEach(item => {
              const li = document.createElement('li');
              li.classList = "flex flex-row flex-nowrap cursor-pointer";
              li.onclick = () => {
                const {type} = chart.config;
                if (type === 'pie' || type === 'doughnut') {
                  // Pie and doughnut charts only have a single dataset and visibility is per item
                  chart.toggleDataVisibility(item.index);
                } else {
                  chart.setDatasetVisibility(item.datasetIndex, !chart.isDatasetVisible(item.datasetIndex));
                }
                chart.update();
              };
        
              // Color box
              const boxSpan = document.createElement('span');
              boxSpan.classList = `inline-block h-[16px] w-[16px] rounded-full mr-2.5 self-center`;
              boxSpan.style.background = item.strokeStyle;
              boxSpan.style.borderColor = item.strokeStyle;
              boxSpan.style.borderWidth = item.lineWidth + 'px';
        
              // Text
              const textContainer = document.createElement('p');
              textContainer.classList = "font-sans text-[0.875rem] text-[#3A3A3A] m-0 p-0";
              textContainer.style.textDecoration = item.hidden ? 'line-through' : '';
        
              const text = document.createTextNode(item.text);
              textContainer.appendChild(text);
        
              li.appendChild(boxSpan);
              li.appendChild(textContainer);
              ul.appendChild(li);
            })
          }
        }
      }
      plugins.push(htmlLegendPlugin);
    }

    return plugins
  }

  async createChart() {
    const chartType = this.refs.chartType ? this.refs.chartType : 'line';
    const chartCanvas = document.querySelector(`${this.selector} canvas`);
    const customLegends = document.querySelectorAll(`${this.selector} .legend-container`);

    let chartData = this.refs.chartData;
    let chartOptions = this.getChartOptions(chartType);
    let chartPlugins = this.getChartPlugins();

    if (chartCanvas) {
      if (customLegends.length > 0) {
        const legendId = `${this.selector}_legend`.replace('#', '')
        for (let i = 0; i < customLegends.length; i++) {
          customLegends[i].id = `${legendId}_${i+1}`;
          if (!chartOptions.plugins['htmlLegend']) chartOptions.plugins['htmlLegend'] = { containerID: [] };
          chartOptions.plugins.htmlLegend.containerID.push(`${legendId}_${i+1}`);
        }
        chartOptions.plugins['legend'] = {
          display: false
        }
      }

      chartData = JSON.parse(chartData);

      new Chart ( chartCanvas, {
        type: chartType,
        options: chartOptions,
        plugins: chartPlugins,
        data: chartData,
      })
    }
  }
}

export default class extends Controller {
  static targets = [ 'cc' ]

  async initialize () {
    for (const div of this.ccTargets) {
      const chart = new CustomChart({
        selector: `#${div.id}`,
      });
      await chart.createChart();
    }
  }
}